import { useState, type FormEvent, type InputHTMLAttributes, type ReactNode } from "react";
import {
  ArrowRight,
  AtSign,
  Eye,
  EyeOff,
  Lock,
  Mail,
  ShieldCheck,
  User,
  type LucideIcon,
} from "lucide-react";
import { OeloxLogo } from "../OeloxLogo";
import { LanguageSelector } from "../LanguageSelector";
import { useI18n } from "../../i18n";

interface AuthShellProps {
  title: string;
  subtitle: string;
  children: ReactNode;
  tag?: string;
}

export function AuthShell({ title, subtitle, children, tag }: AuthShellProps) {
  const { t } = useI18n();
  return (
    <div className="auth-shell auth-shell--compact auth-shell--login">
      <div className="fixed top-4 end-4 z-20">
        <LanguageSelector variant="compact" />
      </div>
      <main className="auth-shell__main auth-shell__main--solo">
        <div className="auth-shell__panel auth-shell__panel--enter">
          <div className="auth-shell__brand-bar">
            <OeloxLogo variant="dark" className="auth-shell__logo-compact" />
            <span className="auth-shell__mobile-tag">{tag ?? t("auth.tag")}</span>
          </div>

          <header className="auth-shell__header">
            <h1 className="auth-shell__title">{title}</h1>
            <p className="auth-shell__subtitle">{subtitle}</p>
          </header>

          <div className="auth-shell__card">{children}</div>

          <p className="auth-shell__foot">{t("auth.foot")}</p>
        </div>
      </main>
    </div>
  );
}

interface AuthFieldProps {
  id: string;
  label: string;
  type?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  icon: LucideIcon;
  autoComplete?: string;
  required?: boolean;
  minLength?: number;
  autoFocus?: boolean;
  inputMode?: InputHTMLAttributes<HTMLInputElement>["inputMode"];
}

export function AuthField({
  id,
  label,
  type = "text",
  value,
  onChange,
  placeholder,
  icon: Icon,
  autoComplete,
  required,
  minLength,
  autoFocus,
  inputMode,
}: AuthFieldProps) {
  const { t } = useI18n();
  const isPassword = type === "password";
  const [revealed, setRevealed] = useState(false);
  const inputType = isPassword ? (revealed ? "text" : "password") : type;

  return (
    <label className="auth-field" htmlFor={id}>
      <span className="auth-field__label">{label}</span>
      <span className="auth-field__control">
        <Icon className="auth-field__icon" strokeWidth={2.25} aria-hidden />
        <input
          id={id}
          type={inputType}
          className={`auth-field__input${isPassword ? " auth-field__input--password" : ""}`}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          autoComplete={autoComplete}
          required={required}
          minLength={minLength}
          autoFocus={autoFocus}
          inputMode={inputMode}
        />
        {isPassword ? (
          <button
            type="button"
            className="auth-field__reveal"
            onClick={() => setRevealed((v) => !v)}
            aria-label={revealed ? t("auth.hidePassword") : t("auth.showPassword")}
            aria-pressed={revealed}
          >
            {revealed ? (
              <EyeOff className="h-4 w-4" strokeWidth={2.25} aria-hidden />
            ) : (
              <Eye className="h-4 w-4" strokeWidth={2.25} aria-hidden />
            )}
          </button>
        ) : null}
      </span>
    </label>
  );
}

export function AuthError({ message }: { message: string }) {
  return (
    <div className="auth-error" role="alert">
      <ShieldCheck className="h-4 w-4" strokeWidth={2.25} />
      <span>{message}</span>
    </div>
  );
}

export function AuthSubmit({
  loading,
  loadingLabel,
  label,
}: {
  loading: boolean;
  loadingLabel: string;
  label: string;
}) {
  return (
    <button type="submit" className="auth-submit" disabled={loading}>
      <span>{loading ? loadingLabel : label}</span>
      {!loading ? <ArrowRight className="h-4 w-4 auth-submit__arrow" strokeWidth={2.5} /> : null}
    </button>
  );
}

export const AUTH_ICONS = {
  mail: Mail,
  lock: Lock,
  user: User,
  atSign: AtSign,
};

export type { FormEvent };
